import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Florida's Top Site Service & Demolition</title>
        <meta name="description" content="Our privacy policy explains how we collect, use, and protect your personal information when you use our website and services." />
        <meta name="keywords" content="privacy policy, data protection, personal information, demolition services, site clearing" />
        <link rel="canonical" href="https://www.floridastopsite.com/privacy-policy" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | Florida's Top Site Service & Demolition" />
        <meta property="og:description" content="Our privacy policy explains how we collect, use, and protect your personal information when you use our website and services." />
        <meta property="og:url" content="https://www.floridastopsite.com/privacy-policy" />
        <meta property="og:image" content="https://www.floridastopsite.com/images/og-image.jpg" />
      </Helmet>
      
      <HeroSection>
        <div className="container">
          <h1>Privacy Policy</h1>
        </div>
      </HeroSection>
      
      <PolicySection>
        <div className="container">
          <div className="policy-content">
            <section>
              <h2>Introduction</h2>
              <p>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p>At Florida's Top Site Service & Demolition, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.</p>
              <p>Please read this privacy policy carefully before using our services.</p>
            </section>
            
            <section>
              <h2>Information We Collect</h2>
              <p>We may collect, use, store, and transfer different kinds of personal data about you which we have grouped together as follows:</p>
              
              <h3>Personal Information</h3>
              <ul>
                <li>Identity Data: includes first name, last name, title</li>
                <li>Contact Data: includes email address, telephone numbers, address</li>
                <li>Transaction Data: includes details about services you have purchased from us</li>
                <li>Technical Data: includes internet protocol (IP) address, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access this website</li>
                <li>Usage Data: includes information about how you use our website and services</li>
                <li>Marketing and Communications Data: includes your preferences in receiving marketing from us and your communication preferences</li>
              </ul>
              
              <h3>Information Collected Automatically</h3>
              <p>When you visit our website, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device. Additionally, as you browse the site, we collect information about the individual web pages that you view, what websites or search terms referred you to the site, and information about how you interact with the site.</p>
            </section>
            
            <section>
              <h2>How We Use Your Information</h2>
              <p>We use the information that we collect to:</p>
              <ul>
                <li>Provide, operate, and maintain our website and services</li>
                <li>Improve, personalize, and expand our website and services</li>
                <li>Understand and analyze how you use our website</li>
                <li>Develop new products, services, features, and functionality</li>
                <li>Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the website, and for marketing and promotional purposes</li>
                <li>Process your transactions</li>
                <li>Send you emails</li>
                <li>Find and prevent fraud</li>
              </ul>
            </section>
            
            <section>
              <h2>Legal Basis for Processing</h2>
              <p>We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:</p>
              <ul>
                <li>Where we need to perform the contract we are about to enter into or have entered into with you</li>
                <li>Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests</li>
                <li>Where we need to comply with a legal obligation</li>
                <li>Where you have provided consent</li>
              </ul>
            </section>
            
            <section>
              <h2>Sharing Your Information</h2>
              <p>We may share your personal information in the following situations:</p>
              <ul>
                <li><strong>With Service Providers:</strong> We may share your information with service providers to monitor and analyze the use of our service, to contact you.</li>
                <li><strong>For Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
                <li><strong>With Your Consent:</strong> We may disclose your personal information for any other purpose with your consent.</li>
              </ul>
            </section>
            
            <section>
              <h2>Cookies Policy</h2>
              <p>Our website uses cookies to enhance your browsing experience. Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to the website owners.</p>
              <p>You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.</p>
            </section>
            
            <section>
              <h2>Data Security</h2>
              <p>We have implemented appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors, and other third parties who have a business need to know.</p>
              <p>However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.</p>
            </section>
            
            <section>
              <h2>Data Retention</h2>
              <p>We will only retain your personal data for as long as reasonably necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, regulatory, tax, accounting, or reporting requirements.</p>
              <p>To determine the appropriate retention period for personal data, we consider the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorized use or disclosure of your personal data, the purposes for which we process your personal data and whether we can achieve those purposes through other means, and the applicable legal, regulatory, tax, accounting, or other requirements.</p>
            </section>
            
            <section>
              <h2>Your Privacy Rights</h2>
              <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
              <ul>
                <li>The right to access your personal data</li>
                <li>The right to request correction of your personal data</li>
                <li>The right to request deletion of your personal data</li>
                <li>The right to object to processing of your personal data</li>
                <li>The right to request restriction of processing your personal data</li>
                <li>The right to request transfer of your personal data</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p>If you wish to exercise any of these rights, please contact us using the information provided in the "Contact Us" section below.</p>
            </section>
            
            <section>
              <h2>Children's Privacy</h2>
              <p>Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we can take necessary actions.</p>
            </section>
            
            <section>
              <h2>Changes to This Privacy Policy</h2>
              <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page.</p>
              <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
            </section>
            
            <section>
              <h2>Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, you can contact us:</p>
              <ul>
                <li>By email: FLSITESERVICE@GMAIL.COM</li>
                <li>By phone: (386) 872-2144 or (512) 767-4123</li>
                <li>By mail: Crescent City, FL 32112</li>
              </ul>
            </section>
          </div>
        </div>
      </PolicySection>
    </>
  );
};

// Styled Components
const HeroSection = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/images/hero-bg.jpg') center/cover no-repeat;
  height: 30vh;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 140, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%);
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
  
  h1 {
    color: white;
    font-size: 4rem;
    font-weight: 700;
    margin: 0;
    text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
`;

const PolicySection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-color);
  
  .container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 2rem;
    display: block;
  }
  
  .policy-content {
    background-color: #f5f5f5;
    border-radius: 10px;
    padding: 3rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    display: block;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding: 2rem;
    }
    
    section {
      margin-bottom: 4rem;
      display: block;
      opacity: 1 !important;
      visibility: visible !important;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    h2 {
      color: #ff8c00;
      font-size: 2.4rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #ff8c00;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 2rem;
      }
    }
    
    h3 {
      color: #333333;
      font-size: 1.8rem;
      font-weight: 600;
      margin: 2rem 0 1rem;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 1.6rem;
      }
    }
    
    p {
      color: #333333;
      font-size: 1.6rem;
      line-height: 1.7;
      margin-bottom: 1.5rem;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 1.5rem;
      }
    }
    
    ul {
      color: #333333;
      padding-left: 2rem;
      margin-bottom: 1.5rem;
      display: block;
      
      li {
        font-size: 1.6rem;
        line-height: 1.7;
        margin-bottom: 0.8rem;
        display: list-item;
        
        @media (max-width: 768px) {
          font-size: 1.5rem;
        }
      }
      
      strong {
        color: #e67e00;
        font-weight: 700;
      }
    }
    
    a {
      color: #ff8c00;
      text-decoration: none;
      font-weight: 600;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export default PrivacyPolicy; 